import React, { useState } from 'react';
import { AlertTriangle, FileText, Globe, Brain, Eye, Settings } from 'lucide-react';
import CountryContextManager from './prompt/CountryContextManager';
import PromptTemplateEditor from './prompt/PromptTemplateEditor';
import AnalysisFrameworkEditor from './prompt/AnalysisFrameworkEditor';
import AnalysisPromptManager from './prompt/AnalysisPromptManager';
import PromptPreview from './prompt/PromptPreview';
import TierConfigurationManager from './prompt/TierConfigurationManager';

type SubTab = 'contexts' | 'templates' | 'frameworks' | 'prompts' | 'tiers' | 'preview';

export default function PromptManagementTab() {
  const [activeSubTab, setActiveSubTab] = useState<SubTab>('contexts');

  const subTabs: { id: SubTab; label: string; icon: React.ReactNode; description: string }[] = [
    {
      id: 'contexts',
      label: 'Country Contexts',
      icon: <Globe className="w-4 h-4" />,
      description: 'Manage country-specific context information',
    },
    {
      id: 'templates',
      label: 'Prompt Templates',
      icon: <FileText className="w-4 h-4" />,
      description: 'Create and manage reusable prompt templates',
    },
    {
      id: 'frameworks',
      label: 'Analysis Frameworks',
      icon: <Brain className="w-4 h-4" />,
      description: 'Define analysis methodologies and scoring systems',
    },
    {
      id: 'prompts',
      label: 'Active Prompts',
      icon: <AlertTriangle className="w-4 h-4" />,
      description: 'Configure active prompts for each country and tier',
    },
    {
      id: 'tiers',
      label: 'Tier Configurations',
      icon: <Settings className="w-4 h-4" />,
      description: 'Configure AI models, token limits, and costs for each tier',
    },
    {
      id: 'preview',
      label: 'Preview & Test',
      icon: <Eye className="w-4 h-4" />,
      description: 'Preview generated prompts and test configurations',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Prompt Management System
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          Dynamically manage AI prompts, country contexts, and analysis frameworks without code changes.
        </p>
      </div>

      {/* Sub-navigation */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
        <div className="flex flex-wrap gap-2">
          {subTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveSubTab(tab.id)}
              className={`
                flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors
                ${
                  activeSubTab === tab.id
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                    : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
                }
              `}
            >
              {tab.icon}
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
        
        {/* Active tab description */}
        <div className="mt-4 text-sm text-gray-600 dark:text-gray-400">
          {subTabs.find(tab => tab.id === activeSubTab)?.description}
        </div>
      </div>

      {/* Content */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
        {activeSubTab === 'contexts' && <CountryContextManager />}
        {activeSubTab === 'templates' && <PromptTemplateEditor />}
        {activeSubTab === 'frameworks' && <AnalysisFrameworkEditor />}
        {activeSubTab === 'prompts' && <AnalysisPromptManager />}
        {activeSubTab === 'tiers' && <TierConfigurationManager />}
        {activeSubTab === 'preview' && <PromptPreview />}
      </div>
    </div>
  );
}